import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Chip, EmptyState, Input, MaskedInput, Modal, PageHeader, Skeleton } from '@koyomi/ui';
import type { ClientListDto } from '../../lib/calendar-api.ts';
import { useBusiness, useSave } from '../../lib/queries.ts';
import { api, apiFull } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import { fmtDateTime } from '../../lib/time.ts';

/** Busca no servidor + páginas por cursor (a chave começa em [negócio, 'clients'] para as invalidações existentes valerem). */
function useClientsPaged(q: string) {
  const businessId = useAuthStore((s) => s.businessId);
  return useInfiniteQuery({
    queryKey: [businessId, 'clients', 'paged', q],
    queryFn: ({ pageParam }) => apiFull<ClientListDto[], { nextCursor: string | null; hasMore: boolean }>('/business/clients', { query: { q: q || undefined, cursor: pageParam || undefined, limit: 50 } }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.meta?.nextCursor ?? undefined,
    placeholderData: (prev) => prev,
  });
}

export function ClientsPage() {
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const navigate = useNavigate();
  const business = useBusiness();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState<{ name: string; phone: string } | null>(null);
  const clients = useClientsPaged(q);
  const rows = clients.data?.pages.flatMap((p) => p.data) ?? [];
  const create = useSave((v: { name: string; phone: string }) => api<{ id: string }>('/business/clients', { body: v }), ['clients']);
  const tz = business.data?.timezone ?? 'UTC';
  return (
    <>
      <PageHeader title={t('calendar:clients.title')} actions={<Button variant="primary" onClick={() => setCreating({ name: '', phone: '' })}>{t('calendar:clients.new')}</Button>} />
      <Input placeholder={t('calendar:clients.search')} value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 420, marginBottom: 16 }} />
      {clients.isLoading || business.isLoading ? <div className="k-card k-card--padded"><Skeleton lines={6} /></div> : clients.data && rows.length === 0 ? <EmptyState title={t('common:status.empty')} description={t('calendar:clients.empty')} /> : (
        <div className="k-table__wrap">
          <table className="k-table">
            <thead><tr><th>{t('calendar:clients.cols.name')}</th><th>{t('calendar:clients.cols.phone')}</th><th>{t('calendar:clients.cols.next')}</th><th>{t('calendar:clients.cols.noShows')}</th><th>{t('calendar:clients.cols.status')}</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/clientes/${c.id}`)}>
                  <td data-label={t('calendar:clients.cols.name')}><strong>{c.name}</strong>{c.tags.map((tag) => <Chip key={tag} tone="neutral" style={{ marginLeft: 6 }}>{tag}</Chip>)}</td>
                  <td data-label={t('calendar:clients.cols.phone')}>{c.phone}</td>
                  <td data-label={t('calendar:clients.cols.next')}>{c.nextAppointmentAt ? fmtDateTime(c.nextAppointmentAt, tz, i18n.language) : '—'}</td>
                  <td data-label={t('calendar:clients.cols.noShows')}>{c.noShowCount}</td>
                  <td data-label={t('calendar:clients.cols.status')}><span className="k-row" style={{ gap: 4 }}>{c.status !== 'ACTIVE' && <Chip tone="danger">{t(`calendar:clients.statuses.${c.status}`)}</Chip>}{c.aiBookingSuspended && <Chip tone="warn">{t('calendar:clients.aiSuspended')}</Chip>}{c.status === 'ACTIVE' && !c.aiBookingSuspended && <Chip tone="accent">{t('calendar:clients.statuses.ACTIVE')}</Chip>}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {clients.hasNextPage && <div style={{ padding: 12, textAlign: 'center' }}><Button onClick={() => clients.fetchNextPage()} loading={clients.isFetchingNextPage}>{t('calendar:clients.more')}</Button></div>}
        </div>
      )}
      {creating && (
        <Modal open onClose={() => setCreating(null)} title={t('calendar:clients.new')} size="sm" footer={<><Button onClick={() => setCreating(null)}>{t('common:actions.cancel')}</Button><Button variant="primary" loading={create.isPending} onClick={() => create.mutate(creating, { onSuccess: (r) => { setCreating(null); navigate(`/clientes/${r.id}`); } })}>{t('common:actions.save')}</Button></>}>
          <div className="k-stack">
            <Input label={t('calendar:clients.name')} value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} autoFocus />
            <MaskedInput mask="phone" label={t('calendar:clients.phone')} value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} />
          </div>
        </Modal>
      )}
    </>
  );
}
